import { ChevronLeft, ChevronRight } from 'lucide-react'

import React from 'react'

import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

interface PaginationProps {
  currentPage: number
  totalItems: number
  pageSize: number
  onPageChange: (page: number) => void
  onPageSizeChange: (size: number) => void
}

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100, 200, 500]

const getPageNumbers = (currentPage: number, totalPages: number) => {
  const pages: (number | string)[] = []

  // If total pages is less than 9, show all pages
  if (totalPages <= 9) {
    return Array.from({ length: totalPages }, (_, i) => i + 1)
  }

  // Always show first page
  pages.push(1)

  // Calculate range around current page
  const leftBound = Math.max(2, currentPage - 2)
  const rightBound = Math.min(totalPages - 1, currentPage + 2)

  // Add ellipsis if there's a gap after first page
  if (leftBound > 2) {
    pages.push('...')
  }

  // Add pages around current page
  for (let i = leftBound; i <= rightBound; i++) {
    pages.push(i)
  }

  // Add ellipsis if there's a gap before last page
  if (rightBound < totalPages - 1) {
    pages.push('...')
  }

  // Always show last page
  pages.push(totalPages)

  return pages
}

export const Pagination = {
  PageSizeDropdown: ({
    pageSize,
    onPageSizeChange,
  }: Pick<PaginationProps, 'pageSize' | 'onPageSizeChange'>) => {
    return (
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground">Show</span>
        <Select
          value={pageSize.toString()}
          onValueChange={(value) => onPageSizeChange(Number(value))}
        >
          <SelectTrigger className="w-[70px] h-8">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PAGE_SIZE_OPTIONS.map((size) => (
              <SelectItem key={size} value={size.toString()}>
                {size}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    )
  },

  Buttons: ({
    currentPage,
    totalItems,
    pageSize,
    onPageChange,
  }: Omit<PaginationProps, 'onPageSizeChange'>) => {
    const totalPages = Math.ceil(totalItems / pageSize)
    const startItem = (currentPage - 1) * pageSize + 1
    const endItem = Math.min(currentPage * pageSize, totalItems)

    if (totalPages <= 1 && totalItems !== 0) return (
      <div className="mt-2 flex items-center justify-between border-t border-border-subtle py-3">
        <div className="text-sm text-muted-foreground">
          All {totalItems.toLocaleString()} entries shown
        </div>
      </div>
    )

    if (totalItems === 0) return null

    return (
      <div className="mt-2 border-t border-border-subtle px-1 pt-3">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="order-2 text-sm text-muted-foreground sm:order-1">
            <span className="hidden sm:inline">Showing </span>
            <span className="font-medium text-ink">{startItem.toLocaleString()}</span> to{' '}
            <span className="font-medium text-ink">{endItem.toLocaleString()}</span> of{' '}
            <span className="font-medium text-ink">{totalItems.toLocaleString()}</span>
            <span className="hidden sm:inline"> entries</span>
          </div>

          <div className="flex items-center gap-1 order-1 sm:order-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-2.5"
            >
              <ChevronLeft className="h-4 w-4 sm:mr-1" />
              <span className="hidden sm:inline">Previous</span>
            </Button>

            <div className="flex items-center gap-1 mx-1">
              {/* Desktop page numbers */}
              <div className="hidden md:flex items-center gap-1">
                {getPageNumbers(currentPage, totalPages).map((page, index) => (
                  <Button
                    key={index}
                    variant={page === currentPage ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => typeof page === 'number' && onPageChange(page)}
                    disabled={page === '...'}
                    className="min-w-7 px-2"
                  >
                    {page}
                  </Button>
                ))}
              </div>

              {/* Mobile current page indicator */}
              <div className="flex h-7 items-center rounded-full bg-fill px-3 text-xs font-medium md:hidden">
                {currentPage} / {totalPages}
              </div>
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-2.5"
            >
              <span className="hidden sm:inline">Next</span>
              <ChevronRight className="h-4 w-4 sm:ml-1" />
            </Button>
          </div>
        </div>
      </div>
    )
  },
}
